import React, { useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import axios from 'axios';
import LinkedInPostPreview from '../../components/LinkedInPostPreview';
import './SharePage.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8085';

interface ShareData {
  token:            string;
  distilledInsight: string;
  topic?:           string;
  toneTags?:        string[];
  hook?:            string;
  outputs?:         Record<string, string>;
  angle?:           string;
  viewCount?:       number;
  likes?:           number;
  dislikes?:        number;
}

type Reaction = 'like' | 'dislike' | null;

const SharePage: React.FC = () => {
  const { token }   = useParams<{ token: string }>();
  const history     = useHistory();
  const [data,      setData]      = useState<ShareData | null>(null);
  const [loading,   setLoading]   = useState(true);
  const [notFound,  setNotFound]  = useState(false);
  const [reaction,  setReaction]  = useState<Reaction>(null);
  const [likes,     setLikes]     = useState(0);
  const [dislikes,  setDislikes]  = useState(0);
  const [reacting,  setReacting]  = useState(false);
  const [thought,   setThought]   = useState('');
  const [sharePdf,  setSharePdf]  = useState<string | null>(null);
  const [pdfSlides, setPdfSlides] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const res  = await axios.get(`${API_URL}/api/share/${token}`);
        const d: ShareData = res.data;
        setData(d);
        setLikes(d.likes ?? 0);
        setDislikes(d.dislikes ?? 0);

        // ── SEO meta — use the LinkedIn post text as the primary content ──
        const postText = d.outputs?.linkedin ?? d.distilledInsight;
        const desc     = postText.slice(0, 155);
        const title    = `${(d.topic ?? 'LinkedIn post')} — Distill`;

        document.title = title;

        const setMeta = (name: string, val: string, prop = false) => {
          const attr = prop ? 'property' : 'name';
          let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
          if (!el) { el = document.createElement('meta'); el.setAttribute(attr, name); document.head.appendChild(el); }
          el.setAttribute('content', val);
        };

        setMeta('description', desc);
        setMeta('keywords', [d.topic, ...(d.toneTags ?? []), 'LinkedIn post', 'distill', 'thought leadership'].filter(Boolean).join(', '));
        setMeta('og:title',       postText.slice(0, 110), true);
        setMeta('og:description', desc,                   true);
        setMeta('og:url',         window.location.href,   true);
        setMeta('og:type',        'article',              true);
        setMeta('og:site_name',   'Distill',              true);
        setMeta('twitter:card',        'summary');
        setMeta('twitter:title',       postText.slice(0, 110));
        setMeta('twitter:description', desc);

        const existing = document.querySelector('script[type="application/ld+json"]');
        if (existing) existing.remove();
        const ld       = document.createElement('script');
        ld.type        = 'application/ld+json';
        ld.text        = JSON.stringify({
          '@context':   'https://schema.org',
          '@type':      'Article',
          headline:     postText.slice(0, 110),
          description:  desc,
          url:          window.location.href,
          publisher:    { '@type': 'Organization', name: 'Distill', url: window.location.origin },
        });
        document.head.appendChild(ld);
      } catch (err: any) {
        if (err?.response?.status === 404) setNotFound(true);
      }
      setLoading(false);
    })();
  }, [token]);

  useEffect(() => {
    if (!data) return;
    axios.get(`${API_URL}/api/share/${token}/pdf`)
      .then(r => {
        setSharePdf(r.data.pdf);
        setPdfSlides(parseInt(r.data.totalSlides ?? '0', 10));
      })
      .catch(() => {});
  }, [data, token]);

  const handleReact = async (r: Reaction) => {
    if (reacting || !r) return;
    const next = reaction === r ? null : r;
    const prev = reaction;
    setReaction(next);
    setLikes(l  => l + (next === 'like'    ? 1 : 0) - (prev === 'like'    ? 1 : 0));
    setDislikes(d => d + (next === 'dislike' ? 1 : 0) - (prev === 'dislike' ? 1 : 0));
    if (next === null) return;
    setReacting(true);
    try {
      const res = await axios.post(`${API_URL}/api/share/${token}/react`, { reaction: next });
      setLikes(res.data.likes);
      setDislikes(res.data.dislikes);
    } catch {
      setReaction(prev);
      setLikes(l  => l - (next === 'like'    ? 1 : 0) + (prev === 'like'    ? 1 : 0));
      setDislikes(d => d - (next === 'dislike' ? 1 : 0) + (prev === 'dislike' ? 1 : 0));
    }
    setReacting(false);
  };

  if (loading) return <div className="sp-loading"><div className="sp-spinner" /></div>;

  if (notFound || !data) {
    return (
      <div className="sp-notfound">
        <div className="sp-notfound-inner">
          <div className="sp-brand">distill<sup>beta</sup></div>
          <h2>This post link has expired or doesn't exist.</h2>
          <button className="sp-cta-btn" onClick={() => history.push('/')}>Create your own post →</button>
        </div>
      </div>
    );
  }

  const postText    = data.outputs?.linkedin ?? data.distilledInsight;
  const pdfBase64   = data.outputs?.pdf || null;
  const totalSlides = parseInt(data.outputs?.totalSlides ?? '0', 10);
  const authorName = data.outputs?.authorName || '';
  const heroText   = data.hook || data.topic || '';

  return (
    <div className="sp-page">

      {/* ── Hero ── */}
      <div className="sp-hero">
        <div className="sp-hero-inner">
          <div className="sp-hero-attribution">
            {authorName
              ? <><span className="sp-hero-author">{authorName}</span><span className="sp-hero-verb"> created a LinkedIn post</span></>
              : <span className="sp-hero-verb">A LinkedIn post · via distill</span>
            }
          </div>
          {heroText && <blockquote className="sp-hero-h">"{heroText}"</blockquote>}
        </div>
      </div>

      <main className="sp-main">
        {/* ── LinkedIn post card ── */}
        <LinkedInPostPreview
          standalone
          postText={postText}
          pdfBase64={sharePdf}
          totalSlides={pdfSlides}
          documentTitle={data.topic}
          userName={authorName || 'Distill'}
        />

        {/* ── Reactions ── */}
        <div className="sp-reactions">
          <span className="sp-reactions-label">Valuable?</span>
          <button
            className={`sp-react-btn${reaction === 'like' ? ' active-like' : ''}`}
            onClick={() => handleReact('like')}
            disabled={reacting}
          >
            👍 {likes > 0 && <span>{likes}</span>}
          </button>
          <button
            className={`sp-react-btn${reaction === 'dislike' ? ' active-dislike' : ''}`}
            onClick={() => handleReact('dislike')}
            disabled={reacting}
          >
            👎 {dislikes > 0 && <span>{dislikes}</span>}
          </button>
        </div>

        {/* ── CTA ── */}
        <div className="sp-cta-section">
          <h2 className="sp-cta-h">Your turn.</h2>
          <div className="sp-cta-input-row">
            <textarea
              className="sp-cta-input"
              placeholder="Drop a rough thought…"
              value={thought}
              onChange={e => setThought(e.target.value)}
              rows={3}
              onKeyDown={e => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && thought.trim()) {
                  history.push(`/distill-v2?q=${encodeURIComponent(thought.trim())}`);
                }
              }}
            />
            <button
              className="sp-cta-btn sp-cta-btn--sm"
              disabled={!thought.trim()}
              onClick={() => history.push(`/distill-v2?q=${encodeURIComponent(thought.trim())}`)}
            >
              Distill it →
            </button>
          </div>
          <div className="sp-cta-note">Free · ~30 seconds</div>
        </div>
      </main>
    </div>
  );
};

export default SharePage;
